import React, { FC, ComponentProps } from 'react';
import styles from './styles.module.css';
import {
  TextInput,
  SelectInput,
  Radio,
  MultiSelectInput,
  ComboBox,
  RadioButtonGroup,
  RadioGroup,
  SelectAutocomplete,
  AddressAutocomplete,
  TextArea,
  LockedTextInput,
  SearchInput,
  ReadOnly,
} from './Variants';
import { Asset, WarningIcon } from '@clearcover/atlas';
import { isPresent } from 'Shared/utils/object';
import classnames from 'classnames';

/*
  1. Add new inputs here, and follow structure below.
*/
type VariantPropMapping =
  | ({
      variant: 'text';
    } & ComponentProps<typeof TextInput>)
  | ({
      variant: 'search';
    } & ComponentProps<typeof SearchInput>)
  | ({
      variant: 'textarea';
    } & ComponentProps<typeof TextArea>)
  | ({
      variant: 'select';
    } & ComponentProps<typeof SelectInput>)
  | ({
      variant: 'radio';
    } & ComponentProps<typeof Radio>)
  | ({
      variant: 'radioGroup';
    } & ComponentProps<typeof RadioGroup>)
  | ({
      variant: 'multiselect';
    } & ComponentProps<typeof MultiSelectInput>)
  | ({
      variant: 'combobox';
    } & ComponentProps<typeof ComboBox>)
  | ({
      variant: 'radioButtonGroup';
    } & ComponentProps<typeof RadioButtonGroup>)
  | ({
      variant: 'selectAutocomplete';
    } & ComponentProps<typeof SelectAutocomplete>)
  | ({
      variant: 'addressAutocomplete';
    } & ComponentProps<typeof AddressAutocomplete>)
  | ({
      variant: 'lockedText';
    } & ComponentProps<typeof LockedTextInput>)
  | ({
      variant: 'readOnly';
    } & ComponentProps<typeof ReadOnly>);

export type FormInputProps = {
  errorMessage?: string;
  renderErrorMessage?: boolean;
  loading?: boolean;
} & VariantPropMapping;

export const FormInput: FC<FormInputProps> = props => (
  <div className={styles.wrapper}>
    {(() => {
      // 2. Add new input as shown below.
      // The switch is required to refine the type of inputProps

      switch (props.variant) {
        case 'text':
          return <TextInput {...props} />;
        case 'search':
          return <SearchInput {...props} />;
        case 'textarea':
          return <TextArea {...props} />;
        case 'select':
          return <SelectInput {...props} />;
        case 'radio':
          return <Radio {...props} />;
        case 'radioGroup':
          return <RadioGroup {...props} />;
        case 'multiselect':
          return <MultiSelectInput {...props} />;
        case 'combobox':
          return <ComboBox {...props} />;
        case 'radioButtonGroup':
          return <RadioButtonGroup {...props} />;
        case 'selectAutocomplete':
          return <SelectAutocomplete {...props} />;
        case 'addressAutocomplete':
          return <AddressAutocomplete {...props} />;
        case 'lockedText':
          return <LockedTextInput {...props} />;
        case 'readOnly':
          return <ReadOnly {...props} />;
        default:
          return null;
      }
    })()}
    {isPresent(props.errorMessage) &&
      props.variant !== 'readOnly' &&
      props.renderErrorMessage !== false && (
        <FormInputErrorMessage {...props} message={props.errorMessage} />
      )}
  </div>
);

export const FormInputErrorMessage = ({
  message,
  className,
  name,
}: {
  message: string;
  className?: string;
  name?: string;
}) => (
  <div
    data-testid={`error-message-for-${name}`}
    className={classnames(styles.error, className)}
  >
    <Asset icon={WarningIcon} height={14} width={14} description="error icon" />
    <div className={styles.errorMessage}>{message}</div>
  </div>
);
